import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, Save, KeyRound, Globe, Copy, ExternalLink } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "@/lib/adminApi";
import { labelCls } from "@/components/admin/Fields";

const inputCls =
  "w-full bg-white border border-[#122A1D]/15 focus:border-[#C8A24A] focus:outline-none text-[#122A1D] px-4 py-2.5 text-sm font-light transition-colors duration-300";

const btnCls =
  "bg-[#C8A24A] text-[#0B1F16] hover:bg-[#B8923D] disabled:opacity-60 font-semibold transition-colors px-6 py-2.5 uppercase tracking-wider text-xs flex items-center gap-2";

const urlRowCls =
  "flex items-center justify-between gap-3 bg-white border border-[#122A1D]/10 px-4 py-3 text-sm";

export const SettingsPanel = () => {
  const [settings, setSettings] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });
  const [pwdSaving, setPwdSaving] = useState(false);

  useEffect(() => {
    adminApi
      .get("/admin/settings")
      .then((r) => setSettings(r.data))
      .catch(() => toast.error("Failed to load settings"));
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const payload = {
        sender_email: settings.sender_email,
        notify_email: settings.notify_email,
        site_url: settings.site_url,
      };
      if (apiKey.trim()) payload.resend_api_key = apiKey.trim();
      const { data } = await adminApi.put("/admin/settings", payload);
      setSettings(data);
      setApiKey("");
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      await adminApi.post("/admin/test-email");
      toast.success("Test email sent — check the notification inbox");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Test email failed");
    } finally {
      setTesting(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwdSaving(true);
    try {
      await adminApi.post("/auth/change-password", pwd);
      setPwd({ current_password: "", new_password: "" });
      toast.success("Password changed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Password change failed");
    } finally {
      setPwdSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Copied to clipboard"));
  };

  if (!settings)
    return (
      <div className="flex items-center gap-2 text-[#5B6F64] py-12">
        <Loader2 size={18} className="animate-spin" /> Loading settings…
      </div>
    );

  return (
    <div data-testid="settings-panel" className="max-w-2xl space-y-12">

      {/* ── Sitemap & SEO ── */}
      <section>
        <h2 className="font-heading text-3xl text-[#122A1D] mb-2">Sitemap &amp; SEO</h2>
        <p className="text-sm text-[#5B6F64] font-light mb-6">
          Set your live domain so the sitemap and robots.txt use the correct URLs. Submit the sitemap URL to
          Google Search Console after deploying.
        </p>
        <div className="space-y-4">
          <div>
            <label className={labelCls}>Production domain (no trailing slash)</label>
            <input
              data-testid="settings-site-url"
              type="url"
              placeholder="https://786i.net"
              value={settings.site_url || ""}
              onChange={(e) => setSettings({ ...settings, site_url: e.target.value })}
              className={inputCls}
            />
          </div>

          <div className="space-y-2">
            <label className={labelCls}>Generated URLs</label>
            <div data-testid="settings-sitemap-url-row" className={urlRowCls}>
              <div className="flex items-center gap-2 min-w-0">
                <Globe size={14} className="text-[#A9842F] shrink-0" />
                <span className="text-[#122A1D] truncate text-xs font-mono">{settings.sitemap_url}</span>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => copyToClipboard(settings.sitemap_url)}
                  data-testid="settings-copy-sitemap-url"
                  className="text-[#5B6F64] hover:text-[#A9842F] transition-colors"
                  title="Copy sitemap URL"
                >
                  <Copy size={14} />
                </button>
                <a
                  href={settings.sitemap_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="settings-open-sitemap"
                  className="text-[#5B6F64] hover:text-[#A9842F] transition-colors"
                  title="Open sitemap"
                >
                  <ExternalLink size={14} />
                </a>
              </div>
            </div>

            <div data-testid="settings-robots-url-row" className={urlRowCls}>
              <div className="flex items-center gap-2 min-w-0">
                <Globe size={14} className="text-[#A9842F] shrink-0" />
                <span className="text-[#122A1D] truncate text-xs font-mono">{settings.robots_url}</span>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => copyToClipboard(settings.robots_url)}
                  data-testid="settings-copy-robots-url"
                  className="text-[#5B6F64] hover:text-[#A9842F] transition-colors"
                  title="Copy robots.txt URL"
                >
                  <Copy size={14} />
                </button>
                <a
                  href={settings.robots_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="settings-open-robots"
                  className="text-[#5B6F64] hover:text-[#A9842F] transition-colors"
                  title="Open robots.txt"
                >
                  <ExternalLink size={14} />
                </a>
              </div>
            </div>
          </div>

          <div className="bg-[#F7F6F1] border border-[#C8A24A]/30 px-4 py-3 text-xs text-[#5B6F64] leading-relaxed">
            <strong className="text-[#8C6A1F]">Cloudflare tip:</strong> Add a Page Rule or Transform Rule to route{" "}
            <code className="bg-white border border-[#122A1D]/10 px-1">786i.net/sitemap.xml</code> →{" "}
            <code className="bg-white border border-[#122A1D]/10 px-1">your-server/api/sitemap.xml</code> so
            Google can find it at the standard path.
          </div>
        </div>
      </section>

      {/* ── Email notifications ── */}
      <section>
        <h2 className="font-heading text-3xl text-[#122A1D] mb-2">Email notifications</h2>
        <p className="text-sm text-[#5B6F64] font-light mb-6">
          Powered by Resend. New contact-form inquiries are emailed to the notification address.
        </p>
        <div className="space-y-4">
          <div>
            <label className={labelCls}>
              Resend API key{" "}
              {settings.resend_configured && (
                <span className="text-[#3E7A4E] normal-case tracking-normal">
                  (configured: {settings.resend_api_key_masked} — leave blank to keep)
                </span>
              )}
            </label>
            <input
              data-testid="settings-resend-key"
              type="password"
              placeholder="re_..."
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Sender email (must be verified in Resend)</label>
            <input
              data-testid="settings-sender-email"
              type="email"
              value={settings.sender_email || ""}
              onChange={(e) => setSettings({ ...settings, sender_email: e.target.value })}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Notification recipient email</label>
            <input
              data-testid="settings-notify-email"
              type="email"
              value={settings.notify_email || ""}
              onChange={(e) => setSettings({ ...settings, notify_email: e.target.value })}
              className={inputCls}
            />
          </div>
          <div className="flex gap-3">
            <button onClick={saveSettings} disabled={saving} data-testid="settings-save-button" className={btnCls}>
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save settings
            </button>
            <button
              onClick={sendTest}
              disabled={testing || !settings.resend_configured}
              data-testid="settings-test-email-button"
              className="border border-[#A9842F] text-[#8C6A1F] hover:bg-[#C8A24A]/10 disabled:opacity-50 transition-colors px-6 py-2.5 uppercase tracking-wider text-xs flex items-center gap-2"
            >
              {testing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Send test email
            </button>
          </div>
        </div>
      </section>

      {/* ── Change password ── */}
      <section>
        <h2 className="font-heading text-3xl text-[#122A1D] mb-6">Change password</h2>
        <form onSubmit={changePassword} className="space-y-4">
          <div>
            <label className={labelCls}>Current password</label>
            <input
              data-testid="settings-current-password"
              type="password"
              required
              value={pwd.current_password}
              onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>New password (min 8 characters)</label>
            <input
              data-testid="settings-new-password"
              type="password"
              required
              minLength={8}
              value={pwd.new_password}
              onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
              className={inputCls}
            />
          </div>
          <button type="submit" disabled={pwdSaving} data-testid="settings-change-password-button" className={btnCls}>
            {pwdSaving ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
            Change password
          </button>
        </form>
      </section>
    </div>
  );
};
